import React, { Fragment, useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PlaceIcon from '@mui/icons-material/Place';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ImageIcon from '@mui/icons-material/Image';
import { Link } from '@mui/material';
import { getTimeRange } from 'utils/helper';
import { resolveColorTheme } from 'utils/colorThemeHelpers';
import BannerImage from 'components/BannerImage';
import { pickTitle, pickDetailDescription } from './EventPreview.helpers';
import { getCardLocales, getDetails, pickDetailsRows } from './detailsSchema';
import { useTranslation } from 'react-i18next';

// Design: details titles carry a trailing colon. Appended at render time only
// (never stored) — ASCII for EN, full-width for zh. A label the operator
// already typed a colon onto (either width) passes through untouched.
const labelWithColon = (label, locale) =>
  /[：:]$/.test(label) ? label : `${label}${locale === 'en' ? ':' : '：'}`;

const EventPreview = (props) => {
  const { record, onJoinClick, ...rest } = props;
  const { i18n } = useTranslation();

  //   console.log('defaultData', defaultData);
  //   console.log('record', record);
  // const domain = 'https://dev-event.ocgl.live';
  const domain = process.env.REACT_APP_ASSET_BASE_URL;
  const rsvpData = record?.rsvp;
  const bannerUrl = rsvpData?.banner
    ? rsvpData.banner.startsWith('http')
      ? rsvpData.banner
      : domain + rsvpData.banner
    : null;
  const websiteLink = rsvpData?.website;
  const email = rsvpData?.email;
  const contactNo = rsvpData?.contactNo;

  // Per-language tab strip over the event's own locales (title keys, canonical
  // order — see EventPreview.helpers.js). The tabs are the PAGE's language
  // control: picking one also switches i18n so the form steps follow. Initial
  // selection honours the browser language when the event enables it.
  //
  // staging-r1 N1: pass getDetails(customization), not the raw customization
  // object — getDetailsLocales guards on Array.isArray, so the raw object
  // made the details leg of the union return [] and details-authored locales
  // never got a tab (the portal call site applies the same wrapper).
  //
  // staging-r1 N2: getCardLocales pins en unconditionally (portal contract,
  // written for the editor). On the public landing a locale with no
  // resolvable title would render the '[Event Name]' placeholder tab, and a
  // 繁-only title would grow a tab strip it never had — so the landing
  // filters locales whose title falls through to the placeholder.
  const cardLocales = getCardLocales(
    record?.title,
    record?.detailDescription,
    getDetails(record?.customization)
  );
  const enabledLocales = cardLocales.filter((l) => pickTitle(record?.title, l) !== '[Event Name]');
  const initialSelected = enabledLocales.includes(i18n.language)
    ? i18n.language
    : enabledLocales.includes('en')
    ? 'en'
    : enabledLocales[0];
  const [selectedLocale, setSelectedLocale] = useState(initialSelected);

  // Clamp: if the selected locale is no longer in the enabled set, fall back
  // to 'en' (or first enabled).
  useEffect(() => {
    if (!enabledLocales.includes(selectedLocale)) {
      setSelectedLocale(enabledLocales.includes('en') ? 'en' : enabledLocales[0]);
    }
  }, [enabledLocales, selectedLocale]);

  const title = pickTitle(record?.title, selectedLocale);
  const pickedDetail = pickDetailDescription(record?.detailDescription, selectedLocale);
  // Event Details rows for the selected tab — per-value EN fallback + empty-row
  // skip (detailsSchema cascade, the reg-portal handoff). Legacy events (no
  // details) resolve to [] → the section stays hidden, rendering exactly as
  // before this section existed.
  const detailsRows = pickDetailsRows(record?.customization, selectedLocale);
  const showTabs = enabledLocales.length >= 2;

  // Operator-configured color theme (customization.event.config.colorTheme).
  // Unset/malformed fields resolve to undefined and MUI's sx drops the keys —
  // the card renders exactly as it did before theming.
  const theme = resolveColorTheme(record);

  // Card chrome (headings, Register Now, dates) renders in the TAB language —
  // the card previews what that language's audience sees.
  const tTab = i18n.getFixedT(selectedLocale, 'translation', 'register');
  const status = record?.status || 'ongoing';

  const addressText = rsvpData?.address?.text?.[selectedLocale] || rsvpData?.address?.text?.en;
  const addressLat = rsvpData?.address?.lat;
  const addressLon = rsvpData?.address?.lon;
  const addressZoom = parseFloat(rsvpData?.address?.z ?? 10).toFixed(0);

  return (
    <Box
      {...rest}
      sx={{
        m: '20px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        paddingBottom: '80px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: 1,
          borderRadius: '10px',
          borderColor: 'divider',
          backgroundColor: theme.backgroundColor,
          color: theme.textColor,
        }}
      >
        {showTabs && (
          <Box
            data-testid="preview-tabs"
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              px: 2,
              py: 1.25,
              fontSize: 12,
            }}
          >
            {enabledLocales.map((l) => {
              const active = l === selectedLocale;
              // Language selector rendered as BUTTONS so it inherits the
              // event's color theme exactly like the Register button below:
              // active = filled (linkButtonColor bg, buttonTextColor label),
              // inactive = outlined in linkButtonColor. Unset theme → MUI
              // defaults (primary teal), matching the unthemed Register button.
              return (
                <Button
                  key={l}
                  size="small"
                  variant={active ? 'contained' : 'outlined'}
                  role="tab"
                  aria-selected={active}
                  data-testid={`preview-tab-${l}`}
                  onClick={() => {
                    setSelectedLocale(l);
                    i18n.changeLanguage(l);
                  }}
                  sx={{
                    minWidth: 44,
                    py: 0.25,
                    px: 1.5,
                    fontSize: 12,
                    fontWeight: active ? 700 : 400,
                    textTransform: 'none',
                    ...(active
                      ? {
                          backgroundColor: theme.linkButtonColor,
                          color: theme.buttonTextColor,
                          '&:hover': { backgroundColor: theme.linkButtonColor },
                        }
                      : {
                          borderColor: theme.linkButtonColor,
                          color: theme.linkButtonColor,
                          '&:hover': {
                            backgroundColor: 'transparent',
                            borderColor: theme.linkButtonColor,
                          },
                        }),
                  }}
                >
                  {l === 'zh-Hans' ? '简' : l === 'zh-Hant' ? '繁' : 'En'}
                </Button>
              );
            })}
          </Box>
        )}
        <BannerImage
          data-testid="preview-banner"
          src={bannerUrl}
          sx={bannerUrl ? undefined : { backgroundColor: '#f5f6f8' }}
        >
          {!bannerUrl && (
            <Box
              aria-label="banner placeholder"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ImageIcon sx={{ fontSize: 72, color: '#b8bec8' }} />
            </Box>
          )}
        </BannerImage>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 2,
          }}
        >
          <Typography
            sx={{
              whiteSpace: 'pre-line',
              wordBreak: 'break-word',
              fontWeight: 500,
              typography: { xs: 'h4', md: 'h3' },
              color: theme.textColor,
            }}
          >
            {title}
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 500, mt: 4 }}>
            {tTab('date-and-time')}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 500,
              display: 'flex',
              alignItems: 'flex-start',
              my: 1,
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: '20px', mr: 1 }} />
            {getTimeRange(record, selectedLocale)}
          </Typography>

          {addressText && (
            <Fragment>
              <Typography variant="h5" sx={{ fontWeight: 500, mt: 4 }}>
                {tTab('venue')}
              </Typography>
              {addressLat && addressLon && (
                <Box
                  id="previewInput_address_embedUrl"
                  sx={{
                    mt: 2,
                    width: ['100%', '500px'],
                    height: '300px',
                    iframe: {
                      border: 0,
                      width: '100%',
                      height: '100%',
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: `<iframe src="https://maps.google.com/maps?q=${addressLat},${addressLon}&hl=${selectedLocale}&z=${addressZoom}&output=embed"/>`,
                  }}
                />
              )}
              <Box
                id="previewInput_address"
                sx={{ display: 'inline-flex', alignItems: 'top', my: 1 }}
              >
                <PlaceIcon sx={{ fontSize: '20px', mr: 1 }} />
                <Typography
                  variant="subtitle2"
                  sx={{ m: 0, whiteSpace: 'pre-line', wordBreak: 'break-word' }}
                >
                  {addressText}
                </Typography>
              </Box>
            </Fragment>
          )}

          {pickedDetail && (
            <Fragment>
              <Typography variant="h5" color="title" sx={{ fontWeight: 500, mt: 4 }}>
                {tTab('about')}
              </Typography>
              <Typography
                sx={{
                  img: { maxWidth: '100%' },
                  // Links in the rich-text description follow the event accent.
                  ...(theme.linkButtonColor ? { '& a': { color: theme.linkButtonColor } } : {}),
                }}
                variant="subtitle1"
                dangerouslySetInnerHTML={{ __html: pickedDetail }}
              />
            </Fragment>
          )}

          {/* Event Details — operator-authored blocks, rendered per the
              selected tab after About (details extend the About narrative).
              Two-column row per the portal design: bold label on the left,
              content on the right. Link rows follow the theme's link/button
              color like the website row below. */}
          {detailsRows.length > 0 && (
            <Fragment>
              <Typography variant="h5" sx={{ fontWeight: 500, mt: 4 }}>
                {tTab('event-details')}
              </Typography>
              {detailsRows.map((row) => (
                <Box
                  key={row.key}
                  data-testid={`preview-details-row-${row.key}`}
                  sx={{ my: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 2 }}
                >
                  {row.label && (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        m: 0,
                        flexBasis: 140,
                        flexShrink: 0,
                        fontWeight: 700,
                        whiteSpace: 'pre-line',
                        wordBreak: 'break-word',
                      }}
                    >
                      {labelWithColon(row.label, selectedLocale)}
                    </Typography>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {row.type === 'link' ? (
                      <Link
                        href={row.url}
                        target="_blank"
                        underline="hover"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          color: theme.linkButtonColor,
                        }}
                      >
                        <Typography variant="subtitle1">{row.displayAsText}</Typography>
                      </Link>
                    ) : row.type === 'textArea' ? (
                      <Typography
                        variant="subtitle1"
                        sx={{
                          m: 0,
                          wordBreak: 'break-word',
                          img: { maxWidth: '100%' },
                          ...(theme.linkButtonColor
                            ? { '& a': { color: theme.linkButtonColor } }
                            : {}),
                          '& p': { my: 0.5 },
                          '& ul': { my: 0.5, pl: 3 },
                        }}
                        dangerouslySetInnerHTML={{ __html: row.content ?? '' }}
                      />
                    ) : (
                      <Typography
                        variant="subtitle1"
                        sx={{ m: 0, whiteSpace: 'pre-line', wordBreak: 'break-word' }}
                      >
                        {row.content}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Fragment>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: ['column', 'row'],
              alignItems: 'left',
              justifyContent: 'space-between',
            }}
          >
            {websiteLink && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', mt: 1 }}>
                <LinkIcon sx={{ fontSize: '20px', mr: 1 }} />
                <Link
                  href={websiteLink}
                  target="_blank"
                  color="inherit"
                  underline="hover"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: theme.linkButtonColor,
                  }}
                >
                  <Typography variant="subtitle2">{websiteLink}</Typography>
                </Link>
              </Box>
            )}

            {contactNo && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', mt: 1 }}>
                <PhoneIcon sx={{ fontSize: '20px', mr: 1 }} />
                <Typography variant="subtitle2">{contactNo}</Typography>
              </Box>
            )}

            {email && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <EmailIcon sx={{ fontSize: '20px', mr: 1 }} />
                <Link
                  href={`mailto:${email}`}
                  color="inherit"
                  underline="hover"
                  sx={{ color: theme.linkButtonColor }}
                >
                  <Typography variant="subtitle2">{email}</Typography>
                </Link>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px',
          backgroundColor: theme.backgroundColor || 'background.paper',
          ...(theme.backgroundColor ? {} : { borderTop: 1, borderColor: 'divider' }),
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {status === 'done' ? (
          <Typography variant="subtitle1" sx={{ textAlign: 'center', color: theme.textColor }}>
            {tTab('registration-completed')}
          </Typography>
        ) : (
          <Button
            variant="outlined"
            onClick={() => onJoinClick()}
            sx={{
              '& .MuiButton-startIcon': { marginRight: '0px' },
              textTransform: 'none',
              width: ['100%', 'auto'],
              backgroundColor: theme.linkButtonColor,
              borderColor: theme.linkButtonColor,
              color: theme.buttonTextColor,
              '&:hover': {
                backgroundColor: theme.linkButtonColor,
                borderColor: theme.linkButtonColor,
                color: theme.buttonTextColor,
              },
            }}
          >
            <Box sx={{ m: 'auto', py: 1, px: 3 }}>{tTab('label-join-now')}</Box>
          </Button>
        )}
      </Box>
    </Box>
  );
};
export default EventPreview;
